import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducers/index";
import { sessionEnd, showSnackbar } from "../../redux/actions/index";

// MaterialUI
import { Card, Box, CssBaseline } from "@mui/material";

// Interface
import { MatriceWrapperClientsData } from "../../interface/matrice";

// Fetch
import { getAll } from "../../fetchs/get";
import { deleteOneMatrix } from "../../fetchs/delete";
import { duplicateMatrix, fetchPostProtect } from "../../fetchs/post";

// Components
import MatrixBox from "../../components/ListMatrix/MatrixBox";
import MatrixDialogEdit from "../../components/ListMatrix/MatrixDialogEdit";
import SearchFilterBar from "../../components/ListMatrix/SearchFilterBar";
import AlerteDialog from "../../components/AlerteDialog/AlerteDialog";
import NavTab from "../../layout/NavTab"; // <--- Import NavTab

const MatrixHome = () => {
  const [allMatrice, setAllMatrice] = useState<Array<MatriceWrapperClientsData>>([]);
  const [filteredMatrice, setFilteredMatrice] = useState<Array<MatriceWrapperClientsData>>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [openModal, setOpenModal] = useState<Boolean>(false);
  const [matrixModal, setMatrixModal] = useState<MatriceWrapperClientsData | undefined>(undefined);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState<boolean>(false);
  const [openForceDeleteConfirmation, setOpenForceDeleteConfirmation] = useState<boolean>(false);
  const [matriceToDelete, setMatriceToDelete] = useState<string>("");
  const [openDuplicateConfirmation, setOpenDuplicateConfirmation] = useState<boolean>(false);
  const [matriceToDuplicate, setMatriceToDuplicate] = useState<string[]>([]);
  
  // Changement ici : useState pour que le re-render fonctionne
  const [displayOnlyOwnMatrice, setDisplayOnlyOwnMatrice] = useState(false);

  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.user.token);
  const author = useSelector((state: RootState) => state.user.username);

  const getAllMatrix = (force = false) => {
    if (token && (allMatrice.length === 0 || force)) {
      getAll(token, "/matrice/all", setAllMatrice, () => dispatch(sessionEnd()), 
        (err: any) => dispatch(showSnackbar("Erreur : " + err?.error, "error"))
      );
    }
  };

  useEffect(() => { getAllMatrix(); }, []);

  // On centralise le filtrage dans un useEffect qui écoute tous les changements
  useEffect(() => {
    const filtered = allMatrice.filter((matrice) => {
      const isOwnMatrice = displayOnlyOwnMatrice ? matrice.creator === author : true;
      const matchesName = matrice.name.toLowerCase().includes(searchTerm.toLowerCase());
      const hasSelectedClient = matrice.clients?.some((client) => selectedClients.includes(client.name));
      return matchesName && (selectedClients.length === 0 || hasSelectedClient) && isOwnMatrice;
    });
    setFilteredMatrice(filtered);
  }, [allMatrice, searchTerm, selectedClients, displayOnlyOwnMatrice, author]);

  const getUniqueClients = (matrices: Array<MatriceWrapperClientsData>) => {
    const clientSet = new Set<string>();
    const unique: any[] = [];
    matrices.forEach((m) => {
      m.clients?.forEach((c) => {
        if (!clientSet.has(c._id)) {
          clientSet.add(c._id);
          unique.push(c);
        }
      });
    });
    return unique;
  };

  const uniqueClients = getUniqueClients(allMatrice);

  // --- HANDLERS ---
  const handleClientClick = (clientName: string) => {
    setSelectedClients([clientName]);
  };

  const handlePersonalMatriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayOnlyOwnMatrice(event.target.checked);
  };

  const handleDeleteMatrice = (force: boolean) => {
    if (token) {
      let url = force ? "/matrice/force" : "/matrice";
      deleteOneMatrix(token, url, matriceToDelete).then((res) => {
        if (res.status === 200) {
          dispatch(showSnackbar("Matrice supprimée", "success"));
          force ? setOpenForceDeleteConfirmation(false) : setOpenDeleteConfirmation(false);
          setAllMatrice(allMatrice.filter((m) => m._id !== matriceToDelete));
        } else if (!force && res.status === 409) {
          setOpenDeleteConfirmation(false);
          setOpenForceDeleteConfirmation(true);
        }
      });
    }
  };

  const handleDuplicateMatrice = () => {
    if (token) {
      duplicateMatrix(token, "/matrice/duplicate", matriceToDuplicate).then((res) => {
        if (res.status === 200) {
          dispatch(showSnackbar("Matrice dupliquée avec succès", "success"));
          setOpenDuplicateConfirmation(false);
          getAllMatrix(true);
        }
      });
    }
  };

  const pushModifiedMatrice = (updatedMatriceData: MatriceWrapperClientsData) => {
    const body = { ...updatedMatriceData, name: updatedMatriceData.name.trim() };
    if (token) {
      fetchPostProtect(token, "/matrice/update", JSON.stringify(body)).then((res) => {
        if (res.status === 200) dispatch(showSnackbar("Modifié avec succès", "success"));
      });
    }
  };

  const updateOneMatrix = (matrice: MatriceWrapperClientsData) => {
    setAllMatrice(allMatrice.map(m => m._id === matrice._id ? { ...m, ...matrice } : m));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", bgcolor: "#f8fafc" }}>
      <CssBaseline />
      
      {/* 1. LE NAVTAB (Titre de la page) */}
      <NavTab text="Bibliothèque des matrices" />

      {/* 2. LA CARD PRINCIPALE (Comme ListProjects) */}
      <Card
        elevation={0}
        sx={{
          mx: 3, mb: 3, p: 2,
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}
      >
        {/* BARRE DE RECHERCHE ET FILTRES */}
        <SearchFilterBar
          handleSearchTermChange={setSearchTerm}
          handleClientSelectionChange={setSelectedClients}
          selectedClients={selectedClients}
          uniqueClients={uniqueClients}
          handlePersonalMatriceChange={handlePersonalMatriceChange}
          updateOneMatrix={updateOneMatrix}
          getAllMatrix={getAllMatrix}
        />

        {/* GRILLE DES MATRICES */}
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "center",
            flexDirection: "row",
            alignItems: "start",
            flexWrap: "wrap",
            overflowY: "auto",
            gap: 2,
            pb: 2
          }}
        >
          {filteredMatrice.map((matrice: MatriceWrapperClientsData) => (
            matrice.type === "template" && (
              <MatrixBox
                key={matrice._id}
                matrice={matrice}
                handleClientClick={handleClientClick}
                setOpenModal={(m: MatriceWrapperClientsData) => { setMatrixModal(m); setOpenModal(true); }}
                handleDeleteConfirmation={(id: string) => { setMatriceToDelete(id); setOpenDeleteConfirmation(true); }}
                // LE BOUTON DUPLIQUER RÉPARÉ ICI
                handleDuplicateConfirmation={() => {
                  setMatriceToDuplicate([matrice._id, matrice.name]);
                  setOpenDuplicateConfirmation(true);
                }}
              />
            )
          ))}
        </Box>
      </Card>

      {/* --- DIALOGUES (Inchangés mais typés) --- */}
      {openModal && matrixModal && (
        <MatrixDialogEdit matrice={matrixModal} setOpenModal={setOpenModal} pushModifiedMatrice={pushModifiedMatrice} updateOneMatrix={updateOneMatrix} />
      )}

      <AlerteDialog open={openDuplicateConfirmation} handleClose={() => setOpenDuplicateConfirmation(false)} deleteProject={handleDuplicateMatrice} title="Dupliquer la matrice" contentText="Souhaitez-vous vraiment dupliquer cette matrice ?" />
      
      <AlerteDialog open={openDeleteConfirmation} handleClose={() => setOpenDeleteConfirmation(false)} deleteProject={() => handleDeleteMatrice(false)} title="Supprimer la matrice" contentText="Cette action est irréversible." />

      <AlerteDialog open={openForceDeleteConfirmation} handleClose={() => setOpenForceDeleteConfirmation(false)} deleteProject={() => handleDeleteMatrice(true)} title="Matrice utilisée !" contentText="Cette matrice est liée à d'autres projets. Forcer la suppression ?" color="error" />
    </Box>
  );
};

export default MatrixHome;